import React from 'react';
import { DollarSign, Users } from 'lucide-react';
import { FIELD_CLASS, LABEL_CLASS } from './CrmFilterUi';

export const INITIAL_BID_PRICING = {
  mode: 'team',
  clientPrice: '',
  internalCost: '',
  scope: '',
  publish: false,
};

function parseMoney(raw) {
  const cleaned = String(raw ?? '').replace(/[$,\s]/g, '');
  if (!cleaned) return '';
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : NaN;
}

/**
 * Turns the form state into the service's `directPricing` option.
 * @returns {{ directPricing: object|null, error: string }}
 */
export function buildDirectPricing(pricing) {
  if (pricing.mode !== 'direct') return { directPricing: null, error: '' };
  const clientListPrice = parseMoney(pricing.clientPrice);
  if (clientListPrice === '' || Number.isNaN(clientListPrice) || clientListPrice < 0) {
    return { directPricing: null, error: 'Enter the client price.' };
  }
  const internalCost = parseMoney(pricing.internalCost);
  if (Number.isNaN(internalCost) || (internalCost !== '' && internalCost < 0)) {
    return { directPricing: null, error: 'Internal cost must be a number (or leave it blank).' };
  }
  return {
    directPricing: { clientListPrice, internalCost, scope: pricing.scope, publish: pricing.publish },
    error: '',
  };
}

const MODES = [
  { value: 'team', label: 'Send to team for pricing', icon: Users },
  { value: 'direct', label: "I'll enter the price", icon: DollarSign },
];

/**
 * "How is this bid priced?" — hand it to the team, or type the price in now.
 * `teamExtras` renders under the team option (e.g. send-now vs draft).
 */
export default function BidPricingFields({ pricing, onChange, team, teamExtras = null }) {
  const set = (patch) => onChange({ ...pricing, ...patch });

  return (
    <div className="space-y-3">
      <span className={LABEL_CLASS}>Pricing</span>
      <div className="grid grid-cols-2 gap-2">
        {MODES.map(({ value, label, icon: Icon }) => {
          const active = pricing.mode === value;
          return (
            <button
              key={value}
              type="button"
              onClick={() => set({ mode: value })}
              className={`flex items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-medium transition ${
                active
                  ? 'border-red-400 bg-red-50 text-red-700 ring-2 ring-red-500/20 dark:border-red-700 dark:bg-red-900/20 dark:text-red-300'
                  : 'border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          );
        })}
      </div>

      {pricing.mode === 'team' ? (
        <>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {team ? `The ${team} team` : 'The team'} gets this in their bid queue and enters their cost.
          </p>
          {teamExtras}
        </>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="bid-client-price" className={LABEL_CLASS}>
                Client price
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-gray-400">$</span>
                <input
                  id="bid-client-price"
                  type="text"
                  inputMode="decimal"
                  value={pricing.clientPrice}
                  onChange={(e) => set({ clientPrice: e.target.value })}
                  placeholder="0.00"
                  className={`${FIELD_CLASS} pl-7 pr-3`}
                />
              </div>
            </div>
            <div>
              <label htmlFor="bid-internal-cost" className={LABEL_CLASS}>
                Internal cost <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-gray-400">$</span>
                <input
                  id="bid-internal-cost"
                  type="text"
                  inputMode="decimal"
                  value={pricing.internalCost}
                  onChange={(e) => set({ internalCost: e.target.value })}
                  placeholder="0.00"
                  className={`${FIELD_CLASS} pl-7 pr-3`}
                />
              </div>
            </div>
          </div>
          <div>
            <label htmlFor="bid-scope" className={LABEL_CLASS}>
              Scope of work <span className="font-normal text-gray-400">(optional — shown on the client's quote)</span>
            </label>
            <textarea
              id="bid-scope"
              rows={3}
              value={pricing.scope}
              onChange={(e) => set({ scope: e.target.value })}
              className={`${FIELD_CLASS} px-3`}
            />
          </div>
          <label className="flex cursor-pointer items-start gap-2.5 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={pricing.publish}
              onChange={(e) => set({ publish: e.target.checked })}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
            />
            <span>
              Publish to the client&apos;s portal now
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                {pricing.publish
                  ? 'The client can see and accept this quote right away.'
                  : 'Saved as Exec review — the client won’t see it yet.'}
              </span>
            </span>
          </label>
        </>
      )}
    </div>
  );
}
